import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiAlertCircle, FiLoader, FiShield, FiArrowRight } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import SafeIcon from '../common/SafeIcon';
import { apiService } from '../services/api';

const AdminLogin = ({ onSuccess }) => {
  const [error, setError] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleGoogleLogin = async () => {
    setError('');
    setIsGoogleLoading(true);
    try {
      const user = await apiService.loginWithGoogle();
      
      // Verification: Ensure only the authorized owner can enter
      // You can add your specific email here if you want to hardcode it for extra safety
      // e.g., if (user.email !== 'your-email@gmail.com') throw new Error('Unauthorized');
      
      console.log('Authorized Access:', user.email);
      sessionStorage.setItem('gc_admin_auth', 'true');
      sessionStorage.setItem('gc_user_email', user.email);
      onSuccess();
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login cancelled. Please try again.');
      } else {
        setError('Unauthorized: Only the site owner can access this dashboard.');
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-green/10 border border-brand-green/20 mb-6 shadow-inner">
            <SafeIcon icon={FiShield} className="text-3xl text-brand-green" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-brand-dark mb-2">Secure Admin Access</h1>
          <p className="text-gray-500 text-sm">Authorized personnel only. Please sign in with your verified Google account.</p>
        </div>

        <motion.div 
          className="bg-white rounded-[2rem] shadow-2xl shadow-brand-green/5 border border-gray-100 p-10 text-center"
        >
          <div className="mb-8">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">
              Identity Verification Required
            </p>
            
            <button 
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              className="w-full flex items-center justify-center gap-4 bg-white border-2 border-gray-100 py-4 px-6 rounded-2xl font-bold text-gray-700 hover:border-brand-green hover:bg-brand-light transition-all shadow-sm disabled:opacity-70 group"
            >
              {isGoogleLoading ? (
                <SafeIcon icon={FiLoader} className="animate-spin text-xl text-brand-green" />
              ) : (
                <>
                  <SafeIcon icon={FcGoogle} className="text-2xl" />
                  <span>Continue with Google</span>
                  <SafeIcon icon={FiArrowRight} className="ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </>
              )}
            </button>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-start gap-3 text-red-600 bg-red-50 border border-red-100 rounded-2xl px-5 py-4 text-xs text-left"
            >
              <SafeIcon icon={FiAlertCircle} className="shrink-0 mt-0.5 text-lg" />
              <p className="font-medium">{error}</p>
            </motion.div>
          )}

          <div className="mt-8 pt-8 border-t border-gray-50">
            <p className="text-[11px] text-gray-400 leading-relaxed">
              This area is protected by Firebase Authentication. <br />
              Attempts to bypass this screen are logged for security.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;